import { takeLatest, put, select, call } from 'redux-saga/effects';
import get from 'lodash/get';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import { ADI_ANALYTICS_DETAILED_ERROR, ADI_ANALYTICS_SRC_CODE } from '../common/Constants';
import ClientSideLoggerAction from '../actions/ClientLoggerAction';
import AnalyticsActionTypes from '../actionTypes/AnalyticsActionTypes';
import { getAccertifyStatus, getDeviceInfo } from '../helpers/Utils/utils';

const context = (state) => state.context;

export function* logKibanaError(errorInfo, actionName, quantumSessionID) {
    yield put(
        ClientSideLoggerAction.logError(
            errorInfo,
            `actionName: | ${actionName} | user-agent:${window?.navigator?.userAgent} | quantumSessionID:${quantumSessionID}`
        )
    );
}

export function* getAccertifyDeviceInfo(action) {
    const {
        actionName = '',
        errorInfo = 'siteProbing Failed to fetch ADI DeviceInfo',
        enableFeatureSpecificFlag,
    } = action;
    const appContext = yield select(context);
    const enableAccertifyADI = get(appContext, 'featureFlags.enableAccertifyADI');
    const isNative = appContext?.isNative;
    const quantumSessionID = Cookies.load('QuantumMetricSessionID') || '';
    const enableADIBrowserDeviceTransactionId = get(
        appContext,
        'featureFlags.enableADIBrowserDeviceTransactionId',
        true
    );

    let result = '';
    if (enableAccertifyADI || enableFeatureSpecificFlag) {
        const deviceInfoResponse = getDeviceInfo(isNative, enableADIBrowserDeviceTransactionId);
        const { deviceInfo, failedToFetchNativeDeviceTransactionId } = deviceInfoResponse;
        if (failedToFetchNativeDeviceTransactionId) {
            yield call(
                logKibanaError,
                'Missed deviceTransactionID from nativeApps and fallback to browser',
                actionName,
                quantumSessionID
            );
        }
        if (Object.keys(deviceInfo).length > 0) {
            result = deviceInfo;
        } else {
            yield call(logKibanaError, errorInfo, actionName, quantumSessionID);
        }
    }
    return result;
}
/** handle ADI Monitoring and success scenerios */
function* handleCustomAnalytics(src, fromPage = '', isTriggerFromLS = false) {
    if (isTriggerFromLS) {
        const analyticsData = {
            eventList: [
                {
                    eventName: 'monResp',
                    parameters: { src },
                    pageInfo: {
                        analyticsPageName: get(
                            window.digitalData,
                            'page.pageInfo.analyticsPageName',
                            fromPage
                        ),
                        pageType: get(window.digitalData, 'page.pageInfo.pageType', ''),
                    },
                },
            ],
        };
        LocalStorage.setData('analytics_PrevPageEvents', analyticsData, true);
    } else {
        yield put({
            type: AnalyticsActionTypes.ADI_SITE_PROBE_SUCCESS_ANALYTICS,
            payload: { eventName: 'monResp', data: { src } },
        });
    }
}

function* handleRejectChallengeAnalytics(
    src,
    fromPage = '',
    isTriggerFromLS = false,
    errorDescription
) {
    const digitalData = typeof window !== 'undefined' ? window?.digitalData : {};
    if (isTriggerFromLS) {
        const analyticsData = {
            error: [
                {
                    errorDescription,
                },
            ],
            eventName: 'formError',
            src,
            page: {
                pageInfo: {
                    analyticsPageName: get(
                        digitalData,
                        'page.pageInfo.analyticsPageName',
                        fromPage
                    ),
                    pageType: get(digitalData, 'page.pageInfo.pageType', ''),
                },
            },
        };
        LocalStorage.setData('analytics_PrevPage', analyticsData, true);
    } else {
        yield put({
            type: AnalyticsActionTypes.FORM_ERROR,
            errorDetails: {
                error: { errorDescription },
                fraudForceErrData: { src },
            },
        });
    }
}

export function* logExtendedAnalytics(action) {
    try {
        /** additional param of adiStatus, traceId, errorMesage
         * are for Initiate checkout flow
         */
        const {
            isTriggerFromLS = false,
            result,
            actionMethod,
            adiStatus = '',
            traceId = '',
            errorMessage = '',
        } = action;
        const digitalData = typeof window !== 'undefined' ? window?.digitalData : {};
        const errorInfo = Array.isArray(result?.response?.data)
            ? result?.response?.data[0]
            : result?.response?.data;

        const traceID =
            result?.response?.headers.get('x-trace-id') || errorInfo?.traceId || traceId;
        const accertifyStatus = adiStatus || result?.response?.headers.get('x-adi-sts');
        const isMonitoringMode = result?.response?.headers.get('x-adi-monitor');
        const quantumSessionID = Cookies.load('QuantumMetricSessionID') || '';
        let ADIStatus;
        ADIStatus = getAccertifyStatus(accertifyStatus).isDenied ? 'REJECT' : ADIStatus;
        ADIStatus = getAccertifyStatus(accertifyStatus).isChallenge ? 'CHALLENGE' : ADIStatus;
        ADIStatus = getAccertifyStatus(accertifyStatus).isApproved ? 'APPROVED' : ADIStatus;

        const errorDescription = errorInfo?.errorMessage || errorMessage || '';
        const src = `${ADI_ANALYTICS_SRC_CODE[`${actionMethod}_${ADIStatus}`]}${
            isMonitoringMode ? 'mon' : ''
        }`;
        const fromPage = ADI_ANALYTICS_DETAILED_ERROR[actionMethod]?.fromPage || '';
        const errMessage = ADI_ANALYTICS_DETAILED_ERROR[actionMethod]?.errMessage;
        const actionName = ADI_ANALYTICS_DETAILED_ERROR[actionMethod]?.actionName;
        const responseCode = result?.response?.status;
        const srcCodeToTriggerMonResp = ['160', '158', '179']; // forgotPassword Enforcement Reject src code
        const triggerMonRespInsteadFormError = srcCodeToTriggerMonResp.includes(src);
        if (ADIStatus) {
            if (isMonitoringMode || ADIStatus === 'APPROVED' || triggerMonRespInsteadFormError) {
                yield call(handleCustomAnalytics, src, fromPage, isTriggerFromLS);
            } else {
                yield call(
                    handleRejectChallengeAnalytics,
                    src,
                    fromPage,
                    isTriggerFromLS,
                    errorDescription
                );
            }

            yield put(
                ClientSideLoggerAction.logError(
                    errMessage,
                    `actionName: | ${actionName} ${ADIStatus} |responseCode: ${responseCode} | user-agent:${
                        typeof window !== 'undefined' && window?.navigator?.userAgent
                    } | quantumSessionID:${quantumSessionID} | traceID:${traceID}`
                )
            );
        }
    } catch (error) {
        console.log('Error while logging Extended Analytics');
    }
}

const watchAccertifyDeviceInfo = function* watchAccertifyDeviceInfo() {
    yield takeLatest('GET_ACCERTIFY_DEVICE_INFO', getAccertifyDeviceInfo);
};
watchAccertifyDeviceInfo.sagaName = 'watchAccertifyDeviceInfo';

const watchLogExtendedAnalytics = function* watchLogExtendedAnalytics() {
    yield takeLatest('LOG_AND_TRIGGER_EXTENDED_ANALYTICS', logExtendedAnalytics);
};
watchLogExtendedAnalytics.sagaName = 'watchLogExtendedAnalytics';

export { watchAccertifyDeviceInfo, watchLogExtendedAnalytics };
